import { DataTable, Dialog, FormSelect } from "@/components";
import { RemoveJointOwnerDialogProps } from "./types";
import { FIRST_NAME, LAST_NAME, OWNERSHIP_SHARE } from "../consts";
import { FormProvider } from "react-hook-form";
import useRemoveJointOwnerDialog from "./useRemoveJointOwnerDialog";
import { TRANSFER_USER_ID } from "./consts";
import { CircleCheck } from "lucide-react";

const RemoveJointOwnerDialog = ({
  open,
  setOpen,
  selectedUser,
  allUsers,
  onSave
}: RemoveJointOwnerDialogProps) => {
  const { form, userOptions, isUserListLoading, hasMore, loadMoreOptions,
    columns, users, totalShare, onSubmit
  } = useRemoveJointOwnerDialog({allUsers, selectedUser, onSave});
  return (
    <Dialog
      open={open}
      setOpen={setOpen}
      title={
        <div className="text-2xl font-bold text-primary">
          {"Remove Joint Owner"}
          <div className="h-1 w-[30%] bg-primary rounded-full" />
        </div>
      }
      contentClassName="lg:!max-w-[700px] lg:!w-[700px]"
      onSave={form.handleSubmit(onSubmit)}
    >
      <FormProvider {...form}>
        <form>
          <span className="text-[16px] text-amber-950 font-medium">
            You are about to remove an owner from this property. Reassign their ownership share before continuing.
          </span>

          <div className="my-5">
            <span className="font-semibold text-gray-600 text-[14px]">1. Owner to Remove</span>
            <div className="mt-3 flex flex-row items-center justify-between border border-gray-300 px-2 py-3 rounded-sm">
              <div className="flex flex-row items-center gap-2">
                <div className="bg-primary flex items-center justify-center rounded-full h-[30px] w-[30px] md:h-[50px] md:w-[50px]">
                  <span className="text-white text-[15px]">{selectedUser[FIRST_NAME]?.charAt(0)?.toUpperCase()}{selectedUser[LAST_NAME]?.charAt(0)?.toUpperCase()}</span>
                </div>
                <div className="flex flex-col">
                  <span className="font-medium">{selectedUser[FIRST_NAME]} {selectedUser[LAST_NAME]}</span>
                  <span className="text-[12px] text-gray-500 font-medium">Current Share</span>
                </div>
              </div>
              <div className="text-[20px]">
                {selectedUser[OWNERSHIP_SHARE]}%
              </div>
            </div>
          </div>

          <div className="my-5 flex flex-col">
            <span className="font-semibold text-gray-600 text-[14px]">2. Transfer Ownership To</span>
            <span className="text-[12px] italic text-slate-500 font-normal">Select an existing owner to transfer the ownership share.</span>
            <div className="mt-4">
              <FormSelect
                name={TRANSFER_USER_ID}
                control={form.control}
                label="Transfer To"
                placeholder="Please select"
                options={userOptions}
                isLoadingMore={isUserListLoading}
                hasMore={hasMore}
                onLoadMore={loadMoreOptions}
              />
            </div>
          </div>

          <div className="my-5 flex flex-col">
            <span className="font-semibold text-gray-600 text-[14px]">3. Ownership Distribution (After Removal)</span>
            <span className="text-[12px] italic text-slate-500 font-normal">Update the shares so the total equals 100%.</span>
            <div className="my-3 border border-gray-100 rounded-lg">
              <DataTable data={users || []} columns={columns} hideFooter className="shadow-none border-0" />
              <div className="p-3 bg-green-100 text-green-600 rounded-b-lg text-[14px] flex flex-row items-center justify-between">
                <div className="flex flex-row items-center gap-2"> <CircleCheck className="stroke-green-600 h-4 w-4" /> Total Ownership </div>
                <div>{totalShare}</div>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </Dialog>
  )
}

export default RemoveJointOwnerDialog;